import { FC, useEffect, useState } from 'react';
import { CardContent } from '@mui/material';
import { TextField } from '@wellstone-solutions/web';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CardHeader } from '@components/ui/Card';
import { Form } from '@modules/auth/components/Form';
import {
  CREATE_USERNAME_FORM_ID,
  USERNAME_ALLOWED_CHARS_REGEX,
} from '@modules/auth/constants';
import { useAuthenticationFlow } from '@modules/auth/hooks/useAuthenticationFlow';
import { useCreateUsername } from '@modules/auth/queries';
import { CreateUsernameSchema } from '@modules/auth/schemas/create-username-schema';
import { useAuthStore } from '@modules/auth/store/auth-store';

import { VerifyUsername } from './VerifyUsername';

import type { CreateUsernameFormType, UsernameStatusType } from './types';

const CreateUsername: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, isPendingScreenName } = useAuthStore();
  const { handleDiscourseUser } = useAuthenticationFlow();
  const mutation = useCreateUsername();
  const screenName = useAuthStore((s) => s.userInfo?.screenName);

  const [rawUsernameStatus, setRawUsernameStatus] =
    useState<UsernameStatusType>('idle');

  const usernameStatus = {
    rawUsernameStatus,
    isChecking: rawUsernameStatus === 'checking',
    isTaken: rawUsernameStatus === 'taken',
    isAvailable: rawUsernameStatus === 'available',
  };

  // Refactor this to a ProtectedRoute
  useEffect(() => {
    if (!isLoggedIn && !isPendingScreenName) navigate('/auth/login');
  }, [isLoggedIn, isPendingScreenName]);

  useEffect(() => {
    const redirectIfPresent = async () => {
      if (screenName) {
        const result = await handleDiscourseUser();

        if (result.redirectUrl) window.location.href = result.redirectUrl;
      }
    };

    redirectIfPresent();
  }, [screenName]);

  const getHelperText = (error: string | undefined) => {
    if (error) return error;

    if (usernameStatus.isChecking)
      return t('auth.create_username.checking', 'Checking...');

    if (usernameStatus.isTaken) {
      return t(
        'auth.create_username.unavailable',
        'This name is already taken. Try a different screen name.'
      );
    }

    if (usernameStatus.isAvailable) {
      return t(
        'auth.create_username.username_available',
        "That's a great name!"
      );
    }

    return t(
      'auth.create_username.helper',
      'This will be the name visible to others in the app.'
    );
  };

  const handleUsernameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    formik: CreateUsernameFormType
  ) => {
    const value = e.target.value.replace(USERNAME_ALLOWED_CHARS_REGEX, '');

    formik.setFieldValue('username', value, true);
  };

  return (
    <>
      <CardHeader
        title={t('auth.create_username.title', 'Personalize Your Account')}
        subheaders={[
          <Trans
            key="auth.create_username.subheader_one"
            i18nKey="auth.create_username.subheader_one">
            What would you like to be called in the app?
          </Trans>,
          <Trans
            key="auth.create_username.subheader_two"
            i18nKey="auth.create_username.subheader_two">
            You can use your name or a nickname.
          </Trans>,
        ]}
      />
      <CardContent sx={styles.cardContent}>
        <Form
          formId={CREATE_USERNAME_FORM_ID}
          schema={CreateUsernameSchema}
          initialValues={{ username: '' }}
          mutation={mutation}
          mutateOptions={{
            onError: (error) => {
              console.error('Failed to create username:', error);
            },
          }}>
          {(form) => {
            const formik = form as CreateUsernameFormType;

            const usernameAvailable =
              formik.dirty && formik.isValid && usernameStatus.isAvailable;

            return (
              <>
                <VerifyUsername
                  formik={formik}
                  setUsernameStatus={setRawUsernameStatus}
                />
                <TextField
                  fullWidth
                  formField="username"
                  label={t('auth.create_username.username_label', 'Username')}
                  id="username"
                  inputProps={{ maxLength: 32 }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleUsernameChange(e, formik)
                  }
                  value={formik.values.username}
                  error={
                    !!formik.errors.username ||
                    (formik.values.username && usernameStatus.isTaken)
                  }
                  helperText={getHelperText(formik.errors.username)}
                  sx={usernameAvailable && styles.success}
                />
              </>
            );
          }}
        </Form>
      </CardContent>
    </>
  );
};

const styles = {
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  success: {
    '& .MuiFormHelperText-root': {
      color: 'success.main',
    },
  },
};

export { CreateUsername };
